import ArchiveRoundedIcon from '@mui/icons-material/ArchiveRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  FormHelperText,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useFormik } from 'formik';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { object, ObjectSchema, string } from 'yup';
import ArticlesApi from '../../../../apis/ArticlesApi';
import ConfirmDialog from '../../../../components/ConfirmDialog/ConfirmDialog';
import FormikTextField from '../../../../components/FormikTextField/FormikTextField';
import LoadingCircle from '../../../../components/LoadingCircle/LoadingCircle';
import UnexpectedError from '../../../../components/UnexpectedError/UnexpectedError';
import SnackbarContext from '../../../../contexts/SnackbarContext';
import { AdminArticleDto } from '../../../../generated-types/adminArticleDto';
import { ArticleUpsertDto } from '../../../../generated-types/articleUpsertDto';
import useApi from '../../../../lib/useApi';
import DataControl, {
  ModifiedDataGridProps,
} from '../../Components/DataControl';
import ArticleGridToolbar from './components/ArticleGridToolbar';
import ArticleRichTextEditor from './components/ArticleRichTextEditor';

const emptyArticleValues: ArticleUpsertDto = {
  title: '',
  authorFirstName: '',
  authorLastName: '',
  altTag: '',
  description: '',
  content: '',
};

const resizeLandscapeImage = async (image: File): Promise<File> => {
  const objectUrl = URL.createObjectURL(image);
  try {
    const imageElement = new Image();
    imageElement.src = objectUrl;
    await imageElement.decode();

    if (imageElement.naturalWidth <= imageElement.naturalHeight) {
      throw new Error('Landscape image required');
    }

    const scale = Math.min(
      1600 / imageElement.naturalWidth,
      900 / imageElement.naturalHeight,
      1
    );
    const width = Math.round(imageElement.naturalWidth * scale);
    const height = Math.round(imageElement.naturalHeight * scale);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) throw new Error('Unable to process image');
    context.drawImage(imageElement, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.86)
    );
    if (!blob) throw new Error('Unable to process image');

    return new File([blob], 'article-image.jpg', { type: 'image/jpeg' });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

const ArticlesSubview = () => {
  const articlesApi = useApi(ArticlesApi);
  const snackbar = useContext(SnackbarContext);
  const [articles, setArticles] = useState<AdminArticleDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [articleToEdit, setArticleToEdit] = useState<AdminArticleDto | null>(
    null
  );
  const [articleToArchive, setArticleToArchive] =
    useState<AdminArticleDto | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | undefined>();
  const [selectedImagePreview, setSelectedImagePreview] = useState('');
  const [imageError, setImageError] = useState('');
  const [authorDefaults, setAuthorDefaults] =
    useState<ArticleUpsertDto>(emptyArticleValues);

  const schema: ObjectSchema<ArticleUpsertDto> = object({
    title: string().defined('Required'),
    authorFirstName: string().defined('Required'),
    authorLastName: string().defined('Required'),
    altTag: string().defined('Required'),
    description: string().defined('Required'),
    content: string().defined('Required'),
  });

  const loadArticles = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const loadedArticles = await articlesApi.GetAdminArticles();
      setArticles(
        loadedArticles
          .filter((article) => !article.archived)
          .sort((a, b) => b.created.getTime() - a.created.getTime())
      );
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [articlesApi]);

  useEffect(() => {
    loadArticles();
  }, [loadArticles]);

  useEffect(() => {
    articlesApi.GetArticleAuthorDefaults().then((defaults) =>
      setAuthorDefaults({
        ...emptyArticleValues,
        authorFirstName: defaults.authorFirstName,
        authorLastName: defaults.authorLastName,
      })
    );
  }, [articlesApi]);

  useEffect(() => {
    return () => {
      if (selectedImagePreview) URL.revokeObjectURL(selectedImagePreview);
    };
  }, [selectedImagePreview]);

  const formik = useFormik<ArticleUpsertDto>({
    initialValues: articleToEdit
      ? {
          title: articleToEdit.title,
          authorFirstName: articleToEdit.authorFirstName,
          authorLastName: articleToEdit.authorLastName,
          altTag: articleToEdit.altTag,
          description: articleToEdit.description,
          content: articleToEdit.content,
        }
      : authorDefaults,
    enableReinitialize: true,
    validationSchema: schema,
    onSubmit: async (values) => {
      if (!articleToEdit && !selectedImage) {
        setImageError('A landscape image is required.');
        formik.setSubmitting(false);
        return;
      }

      try {
        const response = articleToEdit
          ? await articlesApi.UpdateArticle(
              articleToEdit.id,
              values,
              selectedImage
            )
          : await articlesApi.CreateArticle(values, selectedImage!);

        switch (response.code) {
          case 200:
          case 201:
            snackbar.setAlert(
              <Alert severity="success" variant="filled">
                Article saved.
              </Alert>
            );
            snackbar.triggerSnackbar();
            await loadArticles();
            formik.resetForm();
            setShowAddForm(false);
            setArticleToEdit(null);
            setSelectedImage(undefined);
            setImageError('');
            if (selectedImagePreview) URL.revokeObjectURL(selectedImagePreview);
            setSelectedImagePreview('');
            break;
          case 409:
            formik.setFieldError(
              'title',
              'A unique slug could not be created from this title.'
            );
            break;
          case 400:
            setImageError(
              response.body == 'article-image-landscape-required'
                ? 'The image must be landscape.'
                : 'The article image could not be used.'
            );
            break;
          default:
            throw Error();
        }
      } catch {
        snackbar.setAlert(
          <Alert severity="error" variant="filled">
            Article save failed.
          </Alert>
        );
        snackbar.triggerSnackbar();
      } finally {
        formik.setSubmitting(false);
      }
    },
  });

  const resetFormState = useCallback(() => {
    formik.resetForm();
    setShowAddForm(false);
    setArticleToEdit(null);
    setSelectedImage(undefined);
    setImageError('');
    if (selectedImagePreview) URL.revokeObjectURL(selectedImagePreview);
    setSelectedImagePreview('');
  }, [formik, selectedImagePreview]);

  const handleImageSelected = useCallback(
    async (file: File | undefined) => {
      setImageError('');
      setSelectedImage(undefined);
      if (selectedImagePreview) URL.revokeObjectURL(selectedImagePreview);
      setSelectedImagePreview('');
      if (!file) return;

      try {
        const resizedImage = await resizeLandscapeImage(file);
        setSelectedImage(resizedImage);
        setSelectedImagePreview(URL.createObjectURL(resizedImage));
      } catch {
        setImageError('Choose a landscape image.');
      }
    },
    [selectedImagePreview]
  );

  const form = useMemo(
    () => (
      <Container maxWidth="md" sx={{ paddingY: 3 }}>
        <form onSubmit={formik.handleSubmit}>
          <Stack spacing={2}>
            <FormikTextField
              formik={formik}
              fullWidth
              label="Title"
              name="title"
              autoFocus
              helpText="The public article link will be created automatically from this title."
            />
            <FormikTextField
              formik={formik}
              fullWidth
              label="Description"
              name="description"
              multiline
              minRows={2}
            />
            <Box
              display="flex"
              gap="1em"
              flexDirection={{ xs: 'column', md: 'row' }}
            >
              <FormikTextField
                formik={formik}
                fullWidth
                label="Author First Name"
                name="authorFirstName"
              />
              <FormikTextField
                formik={formik}
                fullWidth
                label="Author Last Name"
                name="authorLastName"
              />
            </Box>
            <Box>
              <Box display="flex" alignItems="center" gap="1em" flexWrap="wrap">
                <Button variant="outlined" component="label">
                  Upload Landscape Image
                  <input
                    hidden
                    type="file"
                    accept="image/png,image/jpeg"
                    onChange={(e) => handleImageSelected(e.target.files?.[0])}
                  />
                </Button>
                <Typography color="text.secondary">
                  Use a free landscape image source such as{' '}
                  <Link
                    href="https://unsplash.com"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Unsplash
                  </Link>
                  .
                </Typography>
              </Box>
              {imageError && (
                <FormHelperText error>{imageError}</FormHelperText>
              )}
              {(selectedImagePreview || articleToEdit?.imageUrl) && (
                <Box marginTop="1em">
                  <img
                    src={selectedImagePreview || articleToEdit?.imageUrl}
                    alt={formik.values.altTag}
                    style={{
                      maxWidth: '100%',
                      maxHeight: 220,
                      borderRadius: 4,
                      objectFit: 'contain',
                    }}
                  />
                </Box>
              )}
            </Box>
            <FormikTextField
              formik={formik}
              fullWidth
              label="Image Alt Text"
              name="altTag"
              helpText="Describe the picture for someone who cannot see it, for example: Smiling employee using a laptop in a bright office."
            />
            <Box>
              <Typography variant="h5" gutterBottom>
                Content
              </Typography>
              <ArticleRichTextEditor
                value={formik.values.content}
                disabled={formik.isSubmitting}
                onChange={(html) => formik.setFieldValue('content', html)}
              />
              {formik.touched.content && formik.errors.content && (
                <FormHelperText error>{formik.errors.content}</FormHelperText>
              )}
            </Box>
            <Divider />
            <Box>
              <LoadingButton
                loading={formik.isSubmitting}
                type="submit"
                variant="contained"
              >
                Save Article
              </LoadingButton>
            </Box>
          </Stack>
        </form>
      </Container>
    ),
    [
      articleToEdit,
      formik,
      handleImageSelected,
      imageError,
      selectedImagePreview,
    ]
  );

  const columns: GridColDef[] = [
    {
      field: 'title',
      headerName: 'Title',
      flex: 1,
      minWidth: 220,
    },
    {
      field: 'author',
      headerName: 'Author',
      flex: 0.6,
      minWidth: 150,
      valueGetter: (params) =>
        `${params.row.authorFirstName} ${params.row.authorLastName}`,
    },
    {
      field: 'created',
      headerName: 'Created',
      flex: 0.5,
      minWidth: 150,
      valueFormatter: (params) =>
        params.value.toLocaleString(undefined, {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        }),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      width: 120,
      renderCell: (params) => {
        const row = params.row as AdminArticleDto;
        return (
          <Box display="flex" gap=".25em">
            <Tooltip title="Edit" arrow>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setArticleToEdit(row);
                }}
              >
                <EditRoundedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Archive" arrow>
              <span>
                <IconButton
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    setArticleToArchive(row);
                  }}
                >
                  <ArchiveRoundedIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const dataGridProps: ModifiedDataGridProps = {
    loading: loading,
    getRowId: (row) => row.id,
    onRowClick: (params: any) => setArticleToEdit(params.row),
    slots: {
      toolbar: ArticleGridToolbar,
    },
    slotProps: {
      toolbar: {
        onRefreshClick: loadArticles,
      },
    },
    sx: {
      minWidth: '900px',
      '& .MuiDataGrid-row': {
        cursor: 'pointer',
      },
    },
  };

  return (
    <Box position="relative" height="100%" width="100%">
      {error ? (
        <UnexpectedError errorContext="the articles" />
      ) : (
        <DataControl
          pageTitle="Articles"
          addTitle="Create Article"
          addForm={form}
          editTitle={`Edit - ${articleToEdit?.title}`}
          editForm={form}
          gridColumns={columns}
          rowData={articles}
          dataGridProps={dataGridProps}
          resetForm={resetFormState}
          addShowing={showAddForm}
          showAddForm={() => setShowAddForm(true)}
          hideAdd={resetFormState}
          editShowing={articleToEdit != null}
          hideEdit={resetFormState}
        />
      )}
      <LoadingCircle isLoading={loading && articles.length == 0} />
      <ConfirmDialog
        confirmAction={async () => {
          await articlesApi.ArchiveArticle(articleToArchive!.id);
          setArticles((prev) =>
            prev.filter((article) => article.id != articleToArchive!.id)
          );
          setArticleToArchive(null);
        }}
        confirmColor="error"
        confirmText="Archive"
        dialogBody={`Archive ${articleToArchive?.title}? It will be hidden from the public articles page.`}
        dialogTitle="Archive Article"
        errorMessage="Archiving the article was unsuccessful."
        successMessage="Successfully archived article."
        onClose={() => setArticleToArchive(null)}
        showDialog={articleToArchive != null}
      />
    </Box>
  );
};

export default ArticlesSubview;
